import _ from "underscore";
import React, {FC, useState, useEffect, useMemo} from "react";
import {Container, TextField, Button, Typography, Box} from "@material-ui/core";
import PropertiesEditor from "routes/workflow/workflow_detail/components/action_args/schema/properties_editor";
import ContainedTextField from "routes/workflow/workflow_detail/components/contained_text_field";
import {validateSchema} from "./util/validate_json_schema";

import {OrganisationId} from "common/types/organisation";
import {
  Schema,
  SchemaPropertyWithOptionalType,
} from "common/flowmaster/types/schema";
import {
  WorkflowTask,
  ActionDefinition,
} from "common/flowmaster/types/task_config";
import {
  WorkflowContext,
  WorkflowInputs,
  Prompt,
} from "common/flowmaster/types/workflow";

const styles = {
  legend: {
    border: "1px dashed #777",
    borderRadius: "0.5em",
    padding: "0.5em",
    cursor: "pointer",
  },
  errorBox: {
    backgroundColor: "#ffebee",
    border: "1px solid #f44336",
    borderRadius: "4px",
    padding: "8px 12px",
    marginBottom: "10px",
    color: "#c62828",
    whiteSpace: "pre-wrap",
  },
  unsavedBox: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ff9800",
    borderRadius: "4px",
    padding: "8px 12px",
    marginBottom: "10px",
    color: "#ef6c00",
  },
  previewInput: {
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    overflowX: "auto",
  },
  buttonGroup: {
    display: "flex",
    alignItems: "center",
    gap: ".5em",
    marginTop: ".5em",
  },
} as const;

interface SchemaEditorProps {
  argValue: {value: Schema};
  onUpdateItem: (value: {value: Schema}) => void;
  priorTasks?: WorkflowTask[];
  context?: WorkflowContext;
  inputs?: WorkflowInputs;
  prompts?: Prompt[];
  actionDefinitions?: ActionDefinition[];
  organisationId?: OrganisationId;
  useDataStoreProperties?: boolean;
  showNameEditor?: boolean;
  showDescriptionEditor?: boolean;
  disabled?: boolean;
}

const SchemaEditor: FC<SchemaEditorProps> = ({
  argValue,
  onUpdateItem,
  priorTasks,
  context,
  inputs,
  prompts,
  actionDefinitions,
  organisationId,
  useDataStoreProperties = true,
  showNameEditor = true,
  showDescriptionEditor = true,
  disabled = false,
}) => {
  const [schema, setSchema] = useState<Schema>(argValue.value);
  const [showPreview, setShowPreview] = useState(false);
  const [schemaNameError, setSchemaNameError] = useState<string>("");

  const isParametersOnly = !showNameEditor && !showDescriptionEditor;

  const [previewText, setPreviewText] = useState<string>("");
  const [previewError, setPreviewError] = useState<string>("");
  const [isOutOfSync, setIsOutOfSync] = useState<boolean>(false);
  const [userEditedPreview, setUserEditedPreview] = useState<boolean>(false);

  const [rerenderCount, setRerenderCount] = useState<number>(0);

  const schemaString = useMemo(() => JSON.stringify(schema), [schema]);

  useEffect(() => {
    if (!_.isEqual(schema, argValue.value)) {
      onUpdateItem({value: schema});
    }
  }, [schemaString]);

  useEffect(() => {
    const valueChanged = !_.isEqual(schema, argValue.value);
    if (valueChanged) {
      setSchema(argValue.value);
      setRerenderCount(prev => prev + 1);
      if (!userEditedPreview) {
        const newPreview = renderPreview(
          isParametersOnly ? argValue.value.parameters : argValue.value,
        );
        setPreviewText(newPreview);
        setIsOutOfSync(false);
      }
    }
  }, [argValue.value, isParametersOnly]);

  const currentPreview = useMemo(
    () => renderPreview(isParametersOnly ? schema.parameters : schema),
    [schema, isParametersOnly],
  );

  useEffect(() => {
    if (showPreview && !userEditedPreview) {
      setPreviewText(currentPreview);
      setIsOutOfSync(false);
    }
  }, [showPreview, currentPreview, userEditedPreview]);

  const handlePropertiesChange = (
    properties: Record<string, SchemaPropertyWithOptionalType>,
  ) => {
    const required = Object.keys(properties);
    setSchema(prevSchema => ({
      ...prevSchema,
      parameters: {
        ...prevSchema.parameters,
        properties,
        required,
      },
    }));
  };

  const handleInputChange = (key: string, value: string) => {
    if (key === "name" && !/^[a-zA-Z0-9_-]+$/.test(value)) {
      setSchemaNameError("Schema name contains invalid characters.");
      return;
    }
    setSchemaNameError("");
    setSchema(prev => ({...prev, [key]: value}));
  };

  const handlePreviewTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newText = event.target.value;
    setPreviewText(newText);
    setUserEditedPreview(true);

    if (!newText.trim()) {
      setPreviewError("");
      setIsOutOfSync(false);
      return;
    }

    try {
      const parsedJson = JSON.parse(newText);
      const validationErrors = validateSchema(parsedJson, isParametersOnly);

      if (validationErrors.length > 0) {
        setPreviewError(
          `Schema validation errors:\n• ${validationErrors.join("\n• ")}`,
        );
        setIsOutOfSync(true);
      } else {
        setPreviewError("");
        setIsOutOfSync(newText !== currentPreview);
      }
    } catch (error) {
      setPreviewError("Invalid JSON format");
      setIsOutOfSync(true);
    }
  };

  const handleSyncFromPreview = () => {
    if (previewError || !previewText.trim()) {
      return;
    }

    try {
      const parsedJson = JSON.parse(previewText);
      const validationErrors = validateSchema(parsedJson, isParametersOnly);

      if (validationErrors.length > 0) {
        setPreviewError(
          `Cannot sync - Schema validation errors:\n• ${validationErrors.join(
            "\n• ",
          )}`,
        );
        return;
      }

      if (isParametersOnly) {
        if (parsedJson.properties && typeof parsedJson === "object") {
          setSchema(prev => ({
            ...prev,
            parameters: {
              type: parsedJson.type || "object",
              properties: parsedJson.properties || {},
              required:
                parsedJson.required || Object.keys(parsedJson.properties || {}),
            },
          }));
        }
      } else {
        if (parsedJson.name && parsedJson.parameters) {
          setSchema({
            name: parsedJson.name,
            description: parsedJson.description || "",
            parameters: {
              type: parsedJson.parameters.type || "object",
              properties: parsedJson.parameters.properties || {},
              required:
                parsedJson.parameters.required ||
                Object.keys(parsedJson.parameters.properties || {}),
            },
          });
        }
      }

      setRerenderCount(prev => prev + 1);
      setIsOutOfSync(false);
      setPreviewError("");
      setUserEditedPreview(false);
    } catch (error) {
      setPreviewError("Failed to parse JSON");
    }
  };

  const updatePreviewFromSchema = () => {
    setPreviewText(currentPreview);
    setPreviewError("");
    setIsOutOfSync(false);
    setUserEditedPreview(false);
  };

  const previewRows = useMemo(
    () => Math.max(10, previewText.split("\n").length),
    [previewText],
  );

  return (
    <Container maxWidth="xl" style={{padding: "10px"}}>
      {showNameEditor && !disabled && (
        <div style={{marginBottom: "5px"}}>
          <ContainedTextField
            key={`name-${rerenderCount}`}
            errorMessage={schemaNameError}
            label="Schema function name"
            initialValue={schema.name}
            onUpdate={name => handleInputChange("name", name)}
          />
        </div>
      )}
      {showDescriptionEditor && !disabled && (
        <div style={{marginBottom: "5px"}}>
          <ContainedTextField
            key={`description-${rerenderCount}`}
            label="Description"
            initialValue={schema.description}
            onUpdate={description =>
              handleInputChange("description", description)
            }
          />
        </div>
      )}
      {!disabled && (
        <PropertiesEditor
          key={`properties-${rerenderCount}`}
          properties={schema.parameters.properties}
          onChange={handlePropertiesChange}
          priorTasks={priorTasks}
          context={context}
          inputs={inputs}
          prompts={prompts}
          actionDefinitions={actionDefinitions}
          organisationId={organisationId}
          useDataStoreProperties={useDataStoreProperties}
        />
      )}
      <fieldset style={{borderTop: "1px dashed #777"}}>
        <legend
          style={styles.legend}
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? "Hide " : "Show "}Preview
        </legend>
        {showPreview && (
          <Box>
            {previewError && (
              <Box style={styles.errorBox}>
                <Typography variant="body2">{previewError}</Typography>
              </Box>
            )}
            {isOutOfSync && !previewError && userEditedPreview && (
              <Box style={styles.unsavedBox}>
                <Typography variant="body2">
                  Preview has unsaved changes
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              variant="outlined"
              value={previewText}
              onChange={handlePreviewTextChange}
              rows={previewRows}
              InputProps={{
                style: styles.previewInput,
              }}
              placeholder="Edit JSON here..."
            />

            {userEditedPreview && (
              <div style={styles.buttonGroup}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSyncFromPreview}
                  disabled={Boolean(previewError) || !previewText.trim()}
                >
                  Apply Changes
                </Button>
                <Button variant="outlined" onClick={updatePreviewFromSchema}>
                  Discard Changes
                </Button>
              </div>
            )}
          </Box>
        )}
      </fieldset>
    </Container>
  );
};

function renderPreview(schema: object): string {
  return JSON.stringify(schema, null, 2);
}

export default SchemaEditor;
